// Reusable warehouse-stock table (same AdvanceTable architecture as Project
// Bobin State). Lists everything physically in the warehouse — drums + pallets —
// with location/depot/slot, client-vs-stock, and a "..." action menu:
//   • Free stock     — cancel the delivery, release to free stock
//   • Reserve order  — reserve this stock for an incoming order
//   • Send to truck  — open the load-truck simulator
// Filters: cable, conductors, section, stock/client, type.
// Used in: project-monitor, load-truck-simulator, and warehouse map (per depot
// via the `buildingId` prop).
import { useMemo, useState, forwardRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import {
  Button,
  Dropdown,
  Modal,
  Form,
  Spinner,
  Alert,
  Row,
  Col
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEllipsisVertical,
  faBoxOpen,
  faBookmark,
  faTruck,
  faRotate
} from '@fortawesome/free-solid-svg-icons';

// Phoenix soft badge — renders legibly on BOTH light + dark themes (the plain
// react-bootstrap `bg="*-subtle"` badges came out invisible on this theme).
import Badge from '../base/Badge';
import AdvanceTable from '../base/AdvanceTable';
import AdvanceTableFooter from '../base/AdvanceTableFooter';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import SearchBox from '../common/SearchBox';
import { drumSpecLabel } from '../../features/warehouse/warehouseUtils.js';
import { API } from '../../config/api';
import { formatCoreSpec } from '../../helpers/productLabel';

const ENG = axios.create({ baseURL: API.BACKEND, timeout: 10000 });
ENG.interceptors.request.use(c => {
  const t = localStorage.getItem('token');
  if (t) c.headers['x-auth-token'] = t;
  return c;
});
const REDIS = axios.create({ baseURL: API.REDIS, timeout: 10000 });
REDIS.interceptors.request.use(c => {
  const t = localStorage.getItem('token');
  if (t) c.headers['x-auth-token'] = t;
  return c;
});

// Base product family from the full label ("CYYF 3x2.5" → "CYYF") for the name tabs.
const baseProductName = cn =>
  String(cn || '')
    .replace(/\s*\d+\s*x\s*[\d.]+.*$/i, '')
    .trim();

const COLOR_HEX = {
  BLUE: '#3B82F6', BROWN: '#8B4513', 'GREEN-YELLOW': '#9ACD32', GREY: '#808080',
  GRAY: '#808080', BLACK: '#333', WHITE: '#CCC', RED: '#EF4444', YELLOW: '#F59E0B',
  GREEN: '#22C55E', ORANGE: '#F97316'
};

const DotsToggle = forwardRef(({ onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    className="btn btn-link text-body p-0 border-0"
    onClick={e => {
      e.preventDefault();
      onClick(e);
    }}
    aria-label="actions"
  >
    <FontAwesomeIcon icon={faEllipsisVertical} />
  </button>
));

function useStock(buildingId) {
  return useQuery({
    queryKey: ['warehouse', 'stock', buildingId || 'all'],
    queryFn: async () => {
      const url = buildingId
        ? `/api/v1/warehouse/stock?building=${buildingId}`
        : '/api/v1/warehouse/stock';
      return (await ENG.get(url)).data?.data || [];
    },
    refetchInterval: 20000,
    staleTime: 10000
  });
}

export default function WarehouseStockTable({
  buildingId = null,
  title = 'Warehouse Stock',
  onAddToLoad = null,
  addedIds = []
}) {
  const { data: stock = [], isLoading, error, refetch, isFetching } = useStock(buildingId);
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [fType, setFType] = useState('all'); // all | drum | europallet
  const [fOwner, setFOwner] = useState('all'); // all | stock | client
  const [fCond, setFCond] = useState('all');
  const [fSection, setFSection] = useState('all');
  const [fName, setFName] = useState('all');
  const [banner, setBanner] = useState(null);
  const [reserveRow, setReserveRow] = useState(null);
  const [reserveOrder, setReserveOrder] = useState('');
  const [reserveBusy, setReserveBusy] = useState(false);
  const [truckRow, setTruckRow] = useState(null);
  const [tripSel, setTripSel] = useState('');
  const [newTripCustomer, setNewTripCustomer] = useState('');
  const [truckBusy, setTruckBusy] = useState(false);

  // open trips (draft/loading/loaded) for the "Send to truck" picker
  const { data: openTrips = [], refetch: refetchTrips } = useQuery({
    queryKey: ['warehouse-open-trips'],
    queryFn: async () => {
      const all = (await ENG.get('/api/v1/warehouse/trips')).data?.data || [];
      return all.filter(t => ['draft', 'loading', 'loaded'].includes(t.status));
    },
    staleTime: 15000
  });

  const flash = (variant, msg) => {
    setBanner({ variant, msg });
    setTimeout(() => setBanner(null), 4000);
  };

  // distinct conductors / sections for the filter dropdowns
  const conductorOpts = useMemo(
    () => [...new Set(stock.map(s => s.numberOfConductors).filter(Boolean))].sort((a, b) => a - b),
    [stock]
  );
  const sectionOpts = useMemo(
    () => [...new Set(stock.map(s => s.section).filter(Boolean))].sort((a, b) => a - b),
    [stock]
  );

  // Product-name quick-filter tabs (MYYM, CYYF, CYABYF, …) — the base name without
  // the NxS spec, one tab per product family actually in the warehouse.
  const nameOpts = useMemo(
    () =>
      [
        ...new Set(stock.map(s => baseProductName(s.cableName)).filter(Boolean))
      ].sort(),
    [stock]
  );
  const nameCount = name =>
    stock.filter(s => baseProductName(s.cableName) === name).length;

  // redis orders for the reserve modal
  const { data: orders = [] } = useQuery({
    queryKey: ['redis-orders-for-reserve'],
    queryFn: async () => {
      const d = (await REDIS.get('/api/redis/orders')).data;
      return d?.orders || d?.data || [];
    },
    staleTime: 30000
  });

  const freeStock = async row => {
    try {
      await ENG.post(`/api/v1/warehouse/items/${row.serial}/free-stock`);
      flash('success', `${row.serial} released to free stock`);
      refetch();
    } catch (e) {
      flash('danger', e.response?.data?.error || e.message);
    }
  };

  const sendToTruck = async () => {
    if (!truckRow || !tripSel) return;
    setTruckBusy(true);
    try {
      let code = tripSel;
      if (tripSel === '__new__') {
        const t = (
          await ENG.post('/api/v1/warehouse/trips', {
            customer: newTripCustomer || truckRow.customerName || 'Stock'
          })
        ).data?.data;
        code = t?.code;
        refetchTrips();
      }
      if (!code) throw new Error('Pick or create a trip');
      await ENG.post(`/api/v1/warehouse/trips/${code}/load`, { serial: truckRow.serial });
      flash('success', `${truckRow.serial} loaded onto ${code}`);
      setTruckRow(null);
      setTripSel('');
      setNewTripCustomer('');
      refetch();
      refetchTrips();
    } catch (e) {
      flash('danger', e.response?.data?.error || e.message);
    } finally {
      setTruckBusy(false);
    }
  };

  const doReserve = async () => {
    if (!reserveRow || !reserveOrder) return;
    const ord = orders.find(o => String(o.orderId) === String(reserveOrder));
    setReserveBusy(true);
    try {
      await ENG.post(`/api/v1/warehouse/items/${reserveRow.serial}/reserve-sales`, {
        salesOrderId: ord?.orderId || reserveOrder,
        salesOrderCode: ord?.orderCode || null,
        customerName: ord?.client || null
      });
      flash('success', `${reserveRow.serial} reserved for ${ord?.orderCode || reserveOrder}`);
      setReserveRow(null);
      setReserveOrder('');
      refetch();
    } catch (e) {
      flash('danger', e.response?.data?.error || e.message);
    } finally {
      setReserveBusy(false);
    }
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return stock.filter(s => {
      if (fName !== 'all' && baseProductName(s.cableName) !== fName) return false;
      if (fType !== 'all' && s.type !== fType) return false;
      if (fOwner === 'stock' && (s.customerName || s.reservedForSalesOrderCode)) return false;
      if (fOwner === 'client' && !s.customerName && !s.reservedForSalesOrderCode) return false;
      if (fCond !== 'all' && String(s.numberOfConductors) !== String(fCond)) return false;
      if (fSection !== 'all' && String(s.section) !== String(fSection)) return false;
      if (q && !`${s.cableName} ${s.serial} ${s.tokenId} ${s.customerName || 'stock'} ${s.buildingCode} ${s.slotCode}`
        .toLowerCase().includes(q)) return false;
      return true;
    });
  }, [stock, search, fName, fType, fOwner, fCond, fSection]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'serial',
        header: 'Serial / Token',
        cell: ({ row: { original } }) => (
          <div className="d-flex flex-column">
            <span className="fw-semibold font-monospace fs-9 text-body-emphasis">
              {original.serial}
            </span>
            {original.tokenId &&
              (original.jobId ? (
                <Link
                  to={`/apps/project-management/project-details/${original.jobId}`}
                  className="fs-10 font-monospace text-primary"
                  title="Open job"
                >
                  {original.tokenId}
                </Link>
              ) : (
                <span className="fs-10 text-body-tertiary font-monospace">{original.tokenId}</span>
              ))}
          </div>
        ),
        meta: { headerProps: { style: { minWidth: 150 } }, cellProps: { className: 'py-2' } }
      },
      {
        accessorKey: 'cableName',
        header: 'Product',
        cell: ({ row: { original } }) => {
          const hex = COLOR_HEX[String(original.color || '').toUpperCase()];
          // Spec integrated INTO the label badge ("CYYF 3x2.5"); colour on its own line.
          const baseName = baseProductName(original.cableName) || original.cableName || '—';
          const spec =
            original.numberOfConductors && original.section
              ? formatCoreSpec(original)
              : '';
          const label = spec ? `${baseName} ${spec}` : original.cableName || baseName;
          const color = original.color ? String(original.color).toUpperCase() : '';
          return (
            <div className="d-flex align-items-center gap-2">
              {hex && (
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    background: hex,
                    border: '1px solid rgba(255,255,255,0.25)',
                    flexShrink: 0
                  }}
                />
              )}
              <div className="d-flex flex-column">
                <Badge bg="primary" variant="phoenix" className="fw-bold fs-9 align-self-start">
                  {label}
                </Badge>
                {color && (
                  <span className="text-body-tertiary fs-10 mt-1">{color}</span>
                )}
              </div>
            </div>
          );
        },
        meta: { headerProps: { style: { minWidth: 190 } } }
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row: { original } }) => (
          <div className="d-flex flex-column">
            <Badge
              bg={original.type === 'europallet' ? 'primary' : 'warning'}
              className="align-self-start"
            >
              {original.type === 'europallet' ? 'PALLET' : 'DRUM'}
            </Badge>
            {drumSpecLabel(original) && (
              <span className="text-body-tertiary text-capitalize fs-10 mt-1">
                {drumSpecLabel(original)}
              </span>
            )}
          </div>
        ),
        meta: { headerProps: { style: { width: 120 } } }
      },
      {
        accessorKey: 'lengthMeters',
        header: 'Length',
        cell: ({ row: { original } }) => `${Number(original.lengthMeters || 0).toLocaleString()} m`,
        meta: { headerProps: { style: { width: 100 }, className: 'text-end' }, cellProps: { className: 'text-end' } }
      },
      {
        accessorKey: 'weightKg',
        header: 'Weight',
        cell: ({ row: { original } }) =>
          original.weightKg ? `${Number(original.weightKg).toLocaleString()} kg` : '—',
        meta: { headerProps: { style: { width: 100 }, className: 'text-end' }, cellProps: { className: 'text-end' } }
      },
      {
        accessorKey: 'customerName',
        header: 'For',
        cell: ({ row: { original } }) => {
          const client = original.customerName;
          const resv = original.reservedForSalesOrderCode;
          if (!client && !resv)
            return <Badge bg="success" variant="phoenix" className="fw-bold">STOCK</Badge>;
          const code = resv || original.salesOrderCode;
          return (
            <div className="d-flex flex-column">
              <Badge bg="info" variant="phoenix" className="fw-bold align-self-start">
                {client || 'reserved'}
              </Badge>
              {code &&
                (original.salesOrderId ? (
                  <Link
                    to={`/apps/sales-management/order-details/${original.salesOrderId}`}
                    className="fs-10 font-monospace text-primary mt-1"
                    title="Open order"
                  >
                    {resv ? `↪ ${code}` : code}
                  </Link>
                ) : (
                  <span className="fs-10 text-body-tertiary font-monospace mt-1">
                    {resv ? `↪ ${code}` : code}
                  </span>
                ))}
            </div>
          );
        },
        meta: { headerProps: { style: { minWidth: 130 } } }
      },
      {
        id: 'location',
        header: 'Location',
        cell: ({ row: { original } }) => (
          <div className="d-flex flex-column">
            <span className="fw-semibold">{original.buildingCode || original.locationKind || '—'}</span>
            <span className="fs-10 text-body-tertiary font-monospace">
              {original.slotCode || (original.locationKind === 'yard' ? 'yard' : '—')}
            </span>
          </div>
        ),
        meta: { headerProps: { style: { minWidth: 110 } } }
      },
      {
        id: 'action',
        header: '',
        cell: ({ row: { original } }) => {
          // "Add to load" mode (load-truck step 2) — a single action button.
          if (onAddToLoad) {
            const added = addedIds.includes(original.id);
            return (
              <Button
                size="sm"
                variant={added ? 'outline-success' : 'success'}
                disabled={added}
                onClick={() => onAddToLoad(original)}
              >
                <FontAwesomeIcon icon={faTruck} className="me-1" />
                {added ? 'Added' : 'Add to load'}
              </Button>
            );
          }
          const isStock = !original.customerName && !original.reservedForSalesOrderCode;
          return (
            <Dropdown align="end">
              <Dropdown.Toggle as={DotsToggle} />
              <Dropdown.Menu>
                {!isStock && (
                  <Dropdown.Item onClick={() => freeStock(original)}>
                    <FontAwesomeIcon icon={faBoxOpen} className="me-2" />
                    Cancel delivery → free stock
                  </Dropdown.Item>
                )}
                <Dropdown.Item onClick={() => { setReserveRow(original); setReserveOrder(''); }}>
                  <FontAwesomeIcon icon={faBookmark} className="me-2" />
                  Reserve for order…
                </Dropdown.Item>
                <Dropdown.Item onClick={() => { setTruckRow(original); setTripSel(''); }}>
                  <FontAwesomeIcon icon={faTruck} className="me-2" />
                  Send to truck…
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          );
        },
        meta: { headerProps: { className: 'text-end' }, cellProps: { className: 'text-end' } }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orders, navigate, onAddToLoad, addedIds]
  );

  const table = useAdvanceTable({
    data: rows,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <div className="mb-9 mt-2">
      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
        <h2 className="mb-0">{title}</h2>
        <Badge bg="secondary" variant="phoenix">{rows.length} items</Badge>
        <Button variant="link" size="sm" className="text-body" onClick={() => refetch()}>
          <FontAwesomeIcon icon={faRotate} spin={isFetching} className="me-1" /> Refresh
        </Button>
      </div>

      {/* Quick-filter tabs by product family (MYYM, CYYF, CYABYF, …) */}
      {nameOpts.length > 1 && (
        <div
          className="scrollbar d-flex flex-nowrap gap-2 mb-3 pb-1"
          style={{ overflowX: 'auto' }}
        >
          <button
            type="button"
            onClick={() => setFName('all')}
            className={`btn btn-sm text-nowrap ${
              fName === 'all' ? 'btn-primary' : 'btn-outline-secondary'
            }`}
          >
            All <span className="ms-1 opacity-75">({stock.length})</span>
          </button>
          {nameOpts.map(nm => (
            <button
              key={nm}
              type="button"
              onClick={() => setFName(nm)}
              className={`btn btn-sm text-nowrap ${
                fName === nm ? 'btn-primary' : 'btn-outline-secondary'
              }`}
            >
              {nm} <span className="ms-1 opacity-75">({nameCount(nm)})</span>
            </button>
          ))}
        </div>
      )}

      {banner && <Alert variant={banner.variant} className="py-2">{banner.msg}</Alert>}

      {isLoading ? (
        <div className="py-5 text-center"><Spinner animation="border" size="sm" /> Loading…</div>
      ) : error ? (
        <Alert variant="danger">{String(error.message)}</Alert>
      ) : (
        <AdvanceTableProvider {...table}>
          <Row className="g-2 mb-3 align-items-end">
            <Col xs={12} md="auto"><SearchBox placeholder="Search…" onChange={e => setSearch(e.target.value || '')} /></Col>
            <Col xs={6} md="auto">
              <Form.Select size="sm" value={fType} onChange={e => setFType(e.target.value)}>
                <option value="all">All types</option>
                <option value="drum">Drums</option>
                <option value="europallet">Pallets</option>
              </Form.Select>
            </Col>
            <Col xs={6} md="auto">
              <Form.Select size="sm" value={fOwner} onChange={e => setFOwner(e.target.value)}>
                <option value="all">Stock + Client</option>
                <option value="stock">Stock only</option>
                <option value="client">Client only</option>
              </Form.Select>
            </Col>
            <Col xs={6} md="auto">
              <Form.Select size="sm" value={fCond} onChange={e => setFCond(e.target.value)}>
                <option value="all">All conductors</option>
                {conductorOpts.map(c => <option key={c} value={c}>{c} cond.</option>)}
              </Form.Select>
            </Col>
            <Col xs={6} md="auto">
              <Form.Select size="sm" value={fSection} onChange={e => setFSection(e.target.value)}>
                <option value="all">All sections</option>
                {sectionOpts.map(s => <option key={s} value={s}>{s} mm²</option>)}
              </Form.Select>
            </Col>
          </Row>
          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent">
            <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
            <AdvanceTableFooter pagination />
          </div>
        </AdvanceTableProvider>
      )}

      <Modal show={!!reserveRow} onHide={() => setReserveRow(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-7">Reserve {reserveRow?.serial} for an order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-body-tertiary fs-9">
            {reserveRow?.cableName} · {reserveRow?.type === 'europallet' ? 'pallet' : 'drum'} ·{' '}
            {Number(reserveRow?.lengthMeters || 0).toLocaleString()} m
          </p>
          <Form.Group>
            <Form.Label>Order</Form.Label>
            <Form.Select value={reserveOrder} onChange={e => setReserveOrder(e.target.value)} autoFocus>
              <option value="">Select an order…</option>
              {orders.map(o => (
                <option key={o.orderId} value={o.orderId}>
                  {o.orderCode} — {o.client}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setReserveRow(null)}>Cancel</Button>
          <Button variant="primary" onClick={doReserve} disabled={reserveBusy || !reserveOrder}>
            {reserveBusy ? 'Reserving…' : 'Reserve'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!truckRow} onHide={() => setTruckRow(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-7">Send {truckRow?.serial} to a truck</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-body-tertiary fs-9">
            {truckRow?.cableName} · {Number(truckRow?.lengthMeters || 0).toLocaleString()} m ·{' '}
            {truckRow?.customerName || 'STOCK'}
          </p>
          <Form.Group className="mb-2">
            <Form.Label>Trip</Form.Label>
            <Form.Select value={tripSel} onChange={e => setTripSel(e.target.value)} autoFocus>
              <option value="">Select a trip…</option>
              {openTrips.map(t => (
                <option key={t.code} value={t.code}>
                  {t.code} — {t.customer || 'no client'} · {t.status} · {t.items?.length || 0} items
                </option>
              ))}
              <option value="__new__">➕ New trip…</option>
            </Form.Select>
          </Form.Group>
          {tripSel === '__new__' && (
            <Form.Group>
              <Form.Label>New trip — customer</Form.Label>
              <Form.Control
                value={newTripCustomer}
                placeholder={truckRow?.customerName || 'Client / Stock'}
                onChange={e => setNewTripCustomer(e.target.value)}
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setTruckRow(null)}>Cancel</Button>
          <Button variant="primary" onClick={sendToTruck} disabled={truckBusy || !tripSel}>
            {truckBusy ? 'Loading…' : 'Load onto trip'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
